import { useCallback, useEffect, useMemo, useState } from 'react';
import { notifyIntentFor, type NotifyIntent } from '@/domain/bands';
import { rankStations, type Station, type StationWithDistance } from '@/domain/geo';
import { detectLocale, dictionaryFor, localeFromSearch, type Dictionary, type Locale } from '@/i18n';
import { useLookup, type LookupErrorKind, type LookupResult, type LookupState } from '@/hooks/useLookup';
import { useStations } from '@/hooks/useStations';
import { track } from '@/services/analytics';
import { notifyHostOfLead } from '@/services/embedHost';
import { submitLead, type LeadResult } from '@/services/leads';

const NEARBY_LIST_COUNT = 5;

export type StationFinder = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  dict: Dictionary;
  stations: readonly Station[];
  state: LookupState;
  result: LookupResult | null;
  errorKind: LookupErrorKind | null;
  submitCep: (cep: string) => void;
  useLocation: () => void;
  mapFailed: boolean;
  onMapUnsupported: () => void;
  nearbyStations: readonly StationWithDistance[];
  notifyIntent: NotifyIntent | null;
  submitNotify: (firstName: string, phone: string) => Promise<LeadResult>;
};

/* Everything the page does, with nothing about how it looks. Two surfaces render this:
   the standalone page sales sends over WhatsApp, and the iframe in the vammo.com home.
   They differ only in layout, so the flow lives here once — a second copy of it is how
   the two would drift into answering the same CEP differently. */
export const useStationFinder = (): StationFinder => {
  const [locale, setLocale] = useState<Locale>(
    /* The embed sits in a page that is written in one language, so the host says which,
       and a lead with an English browser does not get an English widget inside a
       Portuguese page. Absent the parameter the browser decides, as it always has. */
    () => localeFromSearch(window.location.search) ?? detectLocale(navigator.languages ?? []),
  );
  const [mapFailed, setMapFailed] = useState(false);
  const dict = dictionaryFor(locale);
  const { stations } = useStations();
  const { state, result, errorKind, submitCep, useLocation } = useLookup({ stations });

  useEffect(() => {
    track('sf_page_view');
  }, []);

  /* index.html ships lang="pt-BR", but the copy follows the browser language, so
     keep the attribute honest for screen readers and translation tools. */
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const onMapUnsupported = useCallback(() => {
    setMapFailed(true);
  }, []);

  /* The lead is asking to be told when the network reaches them, so the answer they were
     looking at travels with the contact: without the CEP and the band, sales sees a
     phone number and no reason to call it. */
  const submitNotify = useCallback(
    async (firstName: string, phone: string): Promise<LeadResult> => {
      if (!result) {
        return 'failed';
      }
      const outcome = await submitLead({
        firstName,
        phone,
        cep: result.cep,
        coords: result.coords,
        band: result.band,
        nearestKm: result.nearest.distanceKm,
        nearestAddress: result.nearest.address,
        inputType: result.cep === null ? 'geolocation' : 'cep',
        locale,
      });
      if (outcome === 'ok') {
        /* Told to the host, not just to our own tag manager. The lead is recorded inside
           the iframe, so a campaign the home optimises for leads never sees this one. */
        notifyHostOfLead();
      }
      return outcome;
    },
    [locale, result],
  );

  const nearbyStations = useMemo(() => {
    if (!result) {
      return [];
    }
    return rankStations(result.coords, stations).slice(0, NEARBY_LIST_COUNT);
  }, [result, stations]);

  return {
    locale,
    setLocale,
    dict,
    stations,
    state,
    result,
    errorKind,
    submitCep,
    useLocation,
    mapFailed,
    onMapUnsupported,
    nearbyStations,
    notifyIntent: result === null ? null : notifyIntentFor(result.band),
    submitNotify,
  };
};
